import {
  ESpinnerSize,
  ESpinnerType,
} from "@/common/icons/icons-common/icons-loading-models";
import { LOADING_SPINNER } from "@/common/icons/icons-common/IconsLoading";
import { cn } from "@/utils/cn";
import { renderIf, renderIfTrue } from "@/utils/react-conditional";
import React from "react";
import checkboxActiveIcon from "./assets/checkbox-active.svg";
import checkboxCheckIcon from "./assets/checkbox-check.svg";
import checkboxIcon from "./assets/checkbox.svg";
import { ECheckboxType } from "./checkbox-models";

export interface ICheckboxConfig {
  type: ECheckboxType;
}

// InputCheckbox.module.scss relied entirely on native `input:checked ~ .x`
// sibling combinators (no JS "checked" state threading needed) plus a
// per-type BEM-ish class combo (`.InputCheckbox.Toggle`, `.InlineBox`,
// etc). Replicated here with Tailwind's `peer`/`peer-checked:` mechanism
// (same pattern established for the InputRadio conversion) and a
// per-type lookup table of pre-merged final class strings, to avoid
// relying on Tailwind's generated-CSS source order to pick a winner
// between same-property utilities (e.g. per-type width/display).
const LABEL_CLASSES: Record<ECheckboxType, string> = {
  [ECheckboxType.BoxList]:
    "relative flex min-h-12 w-full items-center justify-between gap-3 bg-surface px-4 py-3.25 text-left text-body-lg leading-[normal] font-normal text-body-text",
  [ECheckboxType.Box]:
    "relative block text-left w-full text-body-text text-body-lg leading-5.5 font-normal py-1.25 px-4",
  [ECheckboxType.BoxNew]:
    "relative block text-left w-full text-body-text text-body-lg leading-5.5 font-normal",
  [ECheckboxType.InlineBox]:
    "relative block text-left w-full text-body-text text-body-lg leading-5.5 font-normal whitespace-nowrap",
  [ECheckboxType.Toggle]:
    "relative text-body-text text-body-lg leading-5.5 font-normal h-5.5 inline-flex w-auto",
  [ECheckboxType.CircleCheck]:
    "relative text-left text-body-text text-body-lg leading-5.5 font-normal flex items-center",
  [ECheckboxType.NewBox]:
    "relative text-left text-body-text text-body-lg leading-5.5 font-normal flex items-center",
};

const CHECKBOX_WRAPPER_CLASSES: Record<ECheckboxType, string> = {
  [ECheckboxType.BoxList]: "relative block h-5.5 w-5.5 shrink-0 cursor-pointer",
  [ECheckboxType.Box]:
    "float-left relative inline-block align-top w-5.5 h-5.5 cursor-pointer",
  [ECheckboxType.BoxNew]:
    "float-left relative inline-block align-top w-5.5 h-5.5 cursor-pointer",
  [ECheckboxType.InlineBox]:
    "float-left relative inline-block align-top w-5.5 h-5.5 cursor-pointer",
  [ECheckboxType.Toggle]: "",
  [ECheckboxType.CircleCheck]: "flex",
  [ECheckboxType.NewBox]: "inline-flex",
};

const CHECKBOX_LABEL_TEXT_CLASSES: Record<ECheckboxType, string> = {
  [ECheckboxType.BoxList]: "min-w-0 flex-1",
  [ECheckboxType.Box]:
    "text-body-lg inline-block align-top leading-5.5 pl-2.5 w-[calc(100%-32px)]",
  [ECheckboxType.BoxNew]:
    "[font-size:inherit] [line-height:inherit] text-inherit inline-block align-middle pl-2.5 w-[calc(100%-32px)]",
  [ECheckboxType.InlineBox]:
    "whitespace-normal text-body-lg inline-block align-top leading-5.5 pl-2.5",
  [ECheckboxType.Toggle]:
    "text-body-lg leading-5.5 h-5.5 text-text-inverse mr-1.5 peer-checked:text-primary",
  [ECheckboxType.CircleCheck]: "ml-1",
  [ECheckboxType.NewBox]:
    "text-body-lg leading-[normal] font-medium text-body-text ml-2.5",
};

// Figma's check glyph (assets/checkbox-check.svg) sits in a 13.2x12.4 box at
// (4.1, 3.9) of the 22px square and is turned 43.26deg, skewed -3.49deg.
const TICK_CLASS =
  "absolute top-[3.93px] left-[4.08px] z-1 flex h-[12.435px] w-[13.216px] items-center justify-center opacity-0 [transition:opacity_0.25s_ease] peer-checked:opacity-100";

const BACKGROUND_CLASSES: Record<string, string> = {
  default:
    "block absolute top-0 left-0 w-full h-full rounded-sm border-[1.5px] border-border-color z-0 peer-checked:border-primary peer-checked:bg-primary",
  [ECheckboxType.InlineBox]:
    "block absolute top-0 left-0 w-full h-full rounded-sm border-[1.5px] border-border-color z-0 peer-checked:border-primary peer-checked:bg-primary",
};

export const InputCheckbox = React.memo(function InputCheckbox(props: {
  name: string;
  value: string;
  label: React.ReactNode;
  isLoading?: boolean;
  onChange?: (name: string, value: "on" | "off") => void;
  checkboxConfig: ICheckboxConfig;
  additionalClasses?: string[];
  inlineStyle?: React.CSSProperties;
}) {
  const { name, value, label, onChange, checkboxConfig } = props;
  const type = checkboxConfig.type || ECheckboxType.BoxList;
  const isBlackDefault = !!props.additionalClasses?.includes("--blackDefault");
  const isLabelRight = !!props.additionalClasses?.includes("--labelRight");
  const isCentered = !!props.additionalClasses?.includes("--centered");
  const isSettingsToggle =
    !!props.additionalClasses?.includes("settingsToggle");
  const isAvailabilityToggle =
    !!props.additionalClasses?.includes("availabilityToggle");
  const checked = value === "on";

  const labelText = (
    <div
      className={cn(CHECKBOX_LABEL_TEXT_CLASSES[type], {
        "mr-0 h-auto text-body-text":
          type === ECheckboxType.Toggle && isSettingsToggle,
        "leading-[normal]":
          type === ECheckboxType.Toggle && isAvailabilityToggle,
        "font-semibold text-text-inverse":
          type === ECheckboxType.Toggle && isAvailabilityToggle && checked,
        // --labelRight reverses the label/toggle order via flex-row-reverse
        // below, but CHECKBOX_LABEL_TEXT_CLASSES' baked-in mr-1.5 still
        // pushes space to the label's own right -- now the outer edge
        // instead of the gap next to the toggle. Swap it to the opposite
        // side so the gap stays between the two.
        "mr-0 ml-1.5":
          type === ECheckboxType.Toggle && isLabelRight && !isSettingsToggle,
      })}
    >
      {label}
    </div>
  );

  const inputBox = (
    <>
      <input
        id={name}
        className={cn("peer", {
          "opacity-0": type !== ECheckboxType.Toggle,
          "hidden cursor-pointer": type === ECheckboxType.Toggle,
        })}
        type="checkbox"
        name={name}
        checked={checked}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          onChange &&
          onChange(e.currentTarget.name, e.currentTarget.checked ? "on" : "off")
        }
      />
      {renderIfTrue(
        type === ECheckboxType.Box ||
          type === ECheckboxType.BoxNew ||
          type === ECheckboxType.BoxList ||
          type === ECheckboxType.InlineBox,
        () => (
          <>
            <span className={TICK_CLASS}>
              <img
                className="flex-none rotate-[43.26deg] -skew-x-[3.49deg]"
                src={checkboxCheckIcon}
                alt=""
                aria-hidden
              />
            </span>
            <span
              className={
                BACKGROUND_CLASSES[type] || BACKGROUND_CLASSES["default"]
              }
            />
          </>
        )
      )}
      {renderIfTrue(type === ECheckboxType.Toggle, () => (
        <>
          <span
            className={cn(
              "absolute top-0.75 left-0.75 z-1 block h-4 w-4 rounded-[25px] shadow-box-shadow [transition:left_0.25s_ease]",
              // bg-text-inverse (not bg-surface) so the knob stays fixed
              // white regardless of page theme: this default also renders
              // on bg-overlay (a fixed dark scrim over a photo, e.g. the
              // paid-content composer's Free/Paid toggle), where a
              // theme-following knob would go near-black on that scrim.
              isAvailabilityToggle
                ? checked
                  ? "left-5 bg-success"
                  : "bg-text-inverse"
                : isBlackDefault
                  ? "bg-border-strong peer-checked:left-5"
                  : "bg-text-inverse peer-checked:left-5 peer-checked:bg-primary"
            )}
          />
          <span
            className={cn(
              "static block h-5.5 w-9.5 rounded-[14px] border border-border-inverse",
              isAvailabilityToggle
                ? checked
                  ? "border-text-inverse bg-text-inverse"
                  : "border-toggle-off bg-toggle-off"
                : isBlackDefault
                  ? "border-border-strong"
                  : "peer-checked:border peer-checked:border-primary",
              // The off-state track needs its own fill: the knob is white, so
              // on a white surface track it would disappear.
              {
                "border-toggle-off bg-toggle-off peer-checked:bg-surface":
                  isSettingsToggle && !isAvailabilityToggle,
              }
            )}
          />
        </>
      ))}
      {renderIfTrue(type === ECheckboxType.CircleCheck, () => (
        <>
          {checked ? (
            <svg width="23" height="23" viewBox="0 0 23 23" fill="none">
              <circle cx="11.8477" cy="11.9668" r="11" fill="#0094F4" />
              <path
                d="M16.6943 8.4668L9.80469 15.4667"
                stroke="white"
                strokeWidth="2"
                strokeMiterlimit="10"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M9.80339 15.4674L7 12.6191"
                stroke="white"
                strokeWidth="2"
                strokeMiterlimit="10"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <svg width="23" height="23" viewBox="0 0 23 23" fill="none">
              <circle
                cx="11.834"
                cy="11.959"
                r="10.25"
                fill="white"
                stroke="var(--color-radio-border)"
                strokeWidth="1.5"
              />
            </svg>
          )}
        </>
      ))}
      {renderIfTrue(type === ECheckboxType.NewBox, () => (
        <img
          loading="lazy"
          src={checked ? checkboxActiveIcon : checkboxIcon}
          alt=""
          width="23"
          height="23"
        />
      ))}
    </>
  );

  return (
    <label
      data-test="checkbox-label"
      className={cn(LABEL_CLASSES[type], {
        "flex-row-reverse": type === ECheckboxType.Toggle && isLabelRight,
        "justify-center": type === ECheckboxType.NewBox && isCentered,
        "flex h-auto min-h-12 w-full items-center justify-between bg-surface px-4 py-3.25":
          type === ECheckboxType.Toggle && isSettingsToggle,
        "bg-success":
          type === ECheckboxType.Toggle && isAvailabilityToggle && checked,
      })}
      style={props.inlineStyle}
    >
      {renderIfTrue(type === ECheckboxType.Toggle, () => (
        <>
          {labelText}
          <div
            className={cn("relative", {
              "h-5.5 w-9.5 shrink-0": isSettingsToggle,
            })}
          >
            {inputBox}
          </div>
        </>
      ))}
      {renderIfTrue(type === ECheckboxType.BoxList, () => (
        <>
          {labelText}
          <div
            data-test="checkbox-control"
            className={CHECKBOX_WRAPPER_CLASSES[type]}
          >
            {renderIf(!!props.isLoading, {
              ifTrue: () =>
                LOADING_SPINNER(ESpinnerSize.Small)[ESpinnerType.Ios],
              ifFalse: () => inputBox,
            })}
          </div>
        </>
      ))}
      {renderIfTrue(
        type === ECheckboxType.Box ||
          type === ECheckboxType.BoxNew ||
          type === ECheckboxType.InlineBox ||
          type === ECheckboxType.CircleCheck ||
          type === ECheckboxType.NewBox,
        () => (
          <>
            <div className={CHECKBOX_WRAPPER_CLASSES[type]}>{inputBox}</div>
            {labelText}
          </>
        )
      )}
    </label>
  );
});
